import { useState, useCallback } from "react";
import { playScale, stopAudio, getIsPlaying, playNoteWithHighlight, getCurrentlyPlayingScale } from "../lib/audioUtils";
import { clearIntervalHighlight } from "../lib/intervalBrackets";
import { useAudioConfig } from "./useConfig";

/**
 * Custom hook for managing scale audio playback and note interactions
 * Extracts all audio-related logic from ScaleCard component
 * 
 * @param {Array} scale - Array of semitone intervals
 * @param {number} scaleNumber - Unique scale identifier
 * @returns {Object} Audio state and control functions
 */
export const useScaleAudio = (scale, scaleNumber) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentPlayingNote, setCurrentPlayingNote] = useState(-1);
  const audioConfig = useAudioConfig();

  /**
   * Handle individual note clicks - plays single note with highlighting
   */
  const handleNoteClick = useCallback((noteIndex) => {
    // Stop any current playback
    stopAudio();
    setIsPlaying(false);
    setCurrentPlayingNote(-1);
    
    // Clear any interval highlighting from all scale notation containers
    const containers = document.querySelectorAll('.scale-notation');
    containers.forEach(container => clearIntervalHighlight(container));
    
    // Calculate MIDI note for clicked note
    const startingPitch = audioConfig.pitch.startingPitch;
    const midiNote = startingPitch + scale.slice(0, noteIndex + 1).reduce((sum, interval) => sum + interval, 0);
    
    // Find the current scale's notation container and play note with highlighting
    const currentScaleContainer = document.querySelector(`[data-scale-number="${scaleNumber}"] .scale-notation`);
    if (currentScaleContainer) {
      playNoteWithHighlight(midiNote, currentScaleContainer, noteIndex);
    }
  }, [scale, scaleNumber]);

  /**
   * Handle scale playback - plays entire scale with sequential highlighting
   */
  const handlePlayScale = useCallback(async () => {
    const scaleId = `scale-${scaleNumber}`;
    
    if (isPlaying && getCurrentlyPlayingScale() === scaleId) {
      // Stop playback if this scale is currently playing
      stopAudio();
      setIsPlaying(false);
      setCurrentPlayingNote(-1);
      return;
    }
    
    // Stop any other playing scale
    if (getIsPlaying()) {
      stopAudio();
    }
    
    // Clear any interval highlighting
    const currentScaleContainer = document.querySelector(`[data-scale-number="${scaleNumber}"] .scale-notation`);
    if (currentScaleContainer) {
      clearIntervalHighlight(currentScaleContainer);
    }
    
    setIsPlaying(true);
    try {
      await playScale(scale, {
        scaleId,
        // Audio timing parameters come from config
        startingPitch: audioConfig.pitch.startingPitch,
        noteDuration: audioConfig.tempo.noteDuration,
        noteGap: audioConfig.tempo.noteGap,
        playDescending: false,
        container: currentScaleContainer,
        onNoteStart: (noteIndex) => {
          setCurrentPlayingNote(noteIndex);
        },
        onNoteEnd: (noteIndex) => {
          setCurrentPlayingNote(-1);
        },
        onStop: () => {
          setIsPlaying(false);
          setCurrentPlayingNote(-1);
        }
      });
    } catch (error) {
      console.error("Error playing scale:", error);
    } finally {
      setIsPlaying(false);
      setCurrentPlayingNote(-1);
    }
  }, [scale, scaleNumber, isPlaying]);

  // Return the audio state and control functions
  return {
    isPlaying,
    currentPlayingNote,
    handleNoteClick,
    handlePlayScale
  };
};

export default useScaleAudio;